import React, { useState, useEffect, useMemo } from 'react';
import { BetConfig, getGeneralBetConfigs } from '../api/betConfigsApi';
import { getCurrentUser } from '../api/authApi';
import { GeneralBet, getUserGeneralBets, getUserGeneralBetsByUserId, placeGeneralBet, deleteGeneralBet, placeBatchGeneralBets } from '../api/generalBetsApi';
import { getTeams, getRankedTeams, Team } from '../api/teamsApi';
import { getPlayers, Player, searchPlayers } from '../api/playersApi';
import { getGroups, Group } from '../api/groupsApi';
import { getMatches, Match } from '../api/matchesApi';
import { getUsers, User } from '../api/usersApi';
import { Input } from './ui/input';
import SearchDropdown from './SearchDropdown';
import SelectedPickRow from './SelectedPickRow';
import { Badge } from './ui/badge';
import { Check } from 'lucide-react';
import GroupRankingBet from './GroupRankingBet';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

interface GeneralBetsListProps {
  className?: string;
  userId?: number; // Optional: view bets for a specific user (used in info pages)
  bettable?: boolean; // Whether general bets are open for editing
}

const GeneralBetsList: React.FC<GeneralBetsListProps> = ({ className, userId, bettable = false }) => {
  const [betConfigs, setBetConfigs] = useState<BetConfig[]>([]);
  const [userBets, setUserBets] = useState<Record<string, GeneralBet>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Data for selections
  const [teams, setTeams] = useState<Team[]>([]);
  const [rankedTeams, setRankedTeams] = useState<Team[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [groupStandings, setGroupStandings] = useState<Record<string, (number | undefined)[]>>({});
  
  // UI state
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [selectionErrors, setSelectionErrors] = useState<Record<string, string>>({});

  // Modify the state for group selections
  const [groupSelections, setGroupSelections] = useState<Record<string, number[]>>({});

  // New state for player search results
  const [playerSearchResults, setPlayerSearchResults] = useState<Player[]>([]);
  const playerSearchTimeout = React.useRef<NodeJS.Timeout | null>(null);

  // Control which accordion items are open (so we can expand by default in read-only mode)
  const [openItems, setOpenItems] = useState<string[]>([]);

  // Internal user picker (used in read mode when no userId prop is provided)
  const [users, setUsers] = useState<User[]>([]);
  const [internalUserId, setInternalUserId] = useState<number | null>(() => {
    return getCurrentUser()?.id ?? null;
  });
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!userDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target as Node)) {
        setUserDropdownOpen(false);
        setUserSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userDropdownOpen]);

  // tournamentStarted now means "bets are closed / read-only"
  const tournamentStarted = !bettable;

  // In read mode, all bets are collapsed by default
  useEffect(() => {
    setOpenItems([]);
  }, [betConfigs, bettable]);

  // Fetch user list for the internal picker when in read mode and no userId prop
  useEffect(() => {
    if (!tournamentStarted || userId) return;
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const data = await getUsers();
        setUsers(data);
        setInternalUserId(prev => {
          const found = data.find(u => u.id === prev);
          return found ? prev : (data[0]?.id ?? null);
        });
      } catch (err) {
        console.error('Error fetching users for general bets view:', err);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, [tournamentStarted, userId]);

  // Place this useEffect at the top level, not inside loadData
  useEffect(() => {
    // Find if any bet config is for players
    const hasPlayerBet = betConfigs.some(config => config.view_schema?.data === 'players');
    if (!hasPlayerBet) return;
    // Find the first betId for players
    const playerBetId = betConfigs.find(config => config.view_schema?.data === 'players')?.id;
    if (!playerBetId) return;
    const term = searchTerms[playerBetId.toString()] || '';
    if (!term) {
      setPlayerSearchResults([]);
      return;
    }
    if (playerSearchTimeout.current) clearTimeout(playerSearchTimeout.current);
    playerSearchTimeout.current = setTimeout(async () => {
      try {
        const results = await searchPlayers(term, 20, 0);
        setPlayerSearchResults(results);
      } catch (err) {
        setPlayerSearchResults([]);
      }
    }, 300);
    return () => {
      if (playerSearchTimeout.current) clearTimeout(playerSearchTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerms, betConfigs]);

  // Load bet configurations and user bets
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError('');
      
      try {
        // Load all required data
        const [configsData, betsData, teamsData, rankedTeamsData, groupsData, matchesData] = await Promise.all([
          getGeneralBetConfigs(),
          (userId ? getUserGeneralBetsByUserId(userId) : internalUserId ? getUserGeneralBetsByUserId(internalUserId) : getUserGeneralBets()),
          getTeams(),
          getRankedTeams(),
          getGroups(),
          getMatches()
        ]);

        // Filter to only include general bets with view_schema
        const generalConfigs = configsData.filter(
          config => config.general && config.view_schema
        );

        setBetConfigs(generalConfigs);
        setTeams(teamsData);
        setRankedTeams(rankedTeamsData);
        setGroups(groupsData);
        setMatches(matchesData);
        
        // Transform user bets to a map for easier access
        const betsMap: Record<string, GeneralBet> = {};
        betsData.forEach(bet => {
          if (bet.prediction?.id) {
            betsMap[`${bet.bet_id}_${bet.prediction.id}`] = bet;
          } else {
            betsMap[bet.bet_id.toString()] = bet;
          }
        });
        setUserBets(betsMap);
        
        // Initialize group standings from user bets if any
        const initialGroupStandings: Record<string, (number | undefined)[]> = {};
        groupsData.forEach(group => {
          initialGroupStandings[group.id.toString()] = [];
        });
        
        // Find group standings bet and populate initial standings
        const groupStandingsConfig = generalConfigs.find(config => 
          config.view_schema?.data === 'groups'
        );
        
        if (groupStandingsConfig) {
          const groupBets = betsData.filter(bet => bet.bet_id === groupStandingsConfig.id);
          
          // Process each bet to build group standings
          groupBets.forEach(bet => {
            if (bet.prediction && bet.prediction.id) {
              // Find the team to determine its group
              const team = teamsData.find(t => t.id === bet.prediction.id);
              if (team && team.group_id) {
                const groupId = team.group_id.toString();
                
                if (!initialGroupStandings[groupId]) {
                  initialGroupStandings[groupId] = [];
                }
                
                // Add team to standings if not already there
                if (!initialGroupStandings[groupId].includes(team.id)) {
                  initialGroupStandings[groupId].push(team.id);
                }
              }
            }
          });
        }
        
        setGroupStandings(initialGroupStandings);

        // Initialize group selections from user bets if any
        const initialGroupSelections: Record<string, number[]> = {};
        groupsData.forEach(group => {
          initialGroupSelections[group.id.toString()] = [];
        });
        
        // Process group related bets
        if (groupStandingsConfig) {
          // Get all bets for this bet config
          const groupBets = betsData.filter(bet => bet.bet_id === groupStandingsConfig.id);
          
          // Process each bet to build the selections
          groupBets.forEach(bet => {
            if (bet.prediction && bet.prediction.id) {
              // Determine which team this is for
              const team = teamsData.find(t => t.id === bet.prediction.id);
              if (team && team.group_id) {
                const groupId = team.group_id.toString();
                
                if (!initialGroupSelections[groupId]) {
                  initialGroupSelections[groupId] = [];
                }
                
                if (!initialGroupSelections[groupId].includes(team.id)) {
                  initialGroupSelections[groupId].push(team.id);
                }
              }
            }
          });
        }
        
        setGroupSelections(initialGroupSelections);
      } catch (err) {
        console.error('Error loading bet data:', err);
        setError('Failed to load bets. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [userId, internalUserId]);

  // Handle search in team/player selection
  const handleSearch = (betId: string, term: string) => {
    setSearchTerms({ ...searchTerms, [betId]: term });
  };
  
  // Handle team selection
  const handleTeamSelect = async (betId: number, teamId: number) => {
    try {
      setSubmitting(true);
      
      // Get the bet config to check size constraints
      const betConfig = betConfigs.find(config => config.id === betId);
      if (!betConfig || !betConfig.view_schema) {
        setError("Bet configuration not found");
        setSubmitting(false);
        return;
      }
      
      // Find existing selections for this bet
      const existingSelections = Object.values(userBets)
        .filter(bet => bet.bet_id === betId)
        .map(bet => bet.prediction?.id)
        .filter(Boolean);
      
      // Check if this team is already selected
      const isAlreadySelected = existingSelections.includes(teamId);
      
      // Check size constraints
      const requiredSize = betConfig.view_schema.size || 1;
      
      // Create new selections array
      let newSelections: number[] = [];
      
      if (isAlreadySelected) {
        // Remove the team if already selected
        newSelections = existingSelections.filter(id => id !== teamId);
      } else {
        // For single selection, replace the existing selection
        if (requiredSize === 1) {
          newSelections = [teamId];
        } else {
          // For multiple selections, check if we've hit the limit
          if (existingSelections.length >= requiredSize) {
            setSelectionErrors(prev => ({ ...prev, [betId.toString()]: `ניתן לבחור מקסימום ${requiredSize} קבוצות. הסר קבוצה כדי להוסיף אחרת.` }));
            setSearchTerms(prev => ({ ...prev, [betId.toString()]: '' }));
            setSubmitting(false);
            return;
          }
          setSelectionErrors(prev => ({ ...prev, [betId.toString()]: '' }));
          // Add the new selection
          newSelections = [...existingSelections, teamId];
        }
      }
      
      // Create predictions array for batch API
      const predictions = newSelections.map(id => ({ id }));
      
      // If no selections left, use delete endpoint
      if (predictions.length === 0) {
        await deleteGeneralBet(betId);
        
        // Update local state
        const updatedBets = { ...userBets };
        Object.keys(updatedBets).forEach(key => {
          const bet = updatedBets[key];
          if (bet.bet_id === betId) {
            delete updatedBets[key];
          }
        });
        setUserBets(updatedBets);
      } else {
        // Use batch API
        const response = await placeBatchGeneralBets(betId, predictions);
        
        // Update local state
        const updatedBets = { ...userBets };
        
        // First remove existing bets for this bet ID
        Object.keys(updatedBets).forEach(key => {
          const bet = updatedBets[key];
          if (bet.bet_id === betId) {
            delete updatedBets[key];
          }
        });
        
        // Add new bets
        response.forEach((bet, index) => {
          const prediction = predictions[index];
          updatedBets[`${betId}_${prediction.id}`] = {
            ...bet,
            bet_id: betId,
            prediction
          };
        });
        
        setUserBets(updatedBets);
      }
      
      // Clear search term
      setSearchTerms({ ...searchTerms, [betId.toString()]: '' });
    } catch (err) {
      console.error('Error placing bet:', err);
      setError('Failed to save bet. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle player selection
  const handlePlayerSelect = async (betId: number, playerName: string) => {
    try {
      setSubmitting(true);
      // Get the bet config to check size constraints
      const betConfig = betConfigs.find(config => config.id === betId);
      if (!betConfig || !betConfig.view_schema) {
        setError("Bet configuration not found");
        setSubmitting(false);
        return;
      }
      
      // Find the player name
      // const player = players.find(p => p.id === playerId);
      // if (!player) {
      //   setError("Player not found");
      //   setSubmitting(false);
      //   return;
      // }
      // Find existing selections for this bet (by value)
      const existingSelections = Object.values(userBets)
        .filter(bet => bet.bet_id === betId)
        .map(bet => bet.prediction?.value)
        .filter(Boolean);
      // Check if this player is already selected
      const isAlreadySelected = existingSelections.includes(playerName);
      // Check size constraints
      const requiredSize = betConfig.view_schema.size || 1;
      // Create new selections array
      let newSelections: string[] = [];
      if (isAlreadySelected) {
        // Remove the player if already selected
        newSelections = existingSelections.filter(name => name !== playerName);
      } else {
        // For single selection, replace the existing selection
        if (requiredSize === 1) {
          newSelections = [playerName];
        } else {
          // For multiple selections, check if we've hit the limit
          if (existingSelections.length >= requiredSize) {
            setSelectionErrors(prev => ({ ...prev, [betId.toString()]: `ניתן לבחור מקסימום ${requiredSize} שחקנים. הסר שחקן כדי להוסיף אחר.` }));
            setSubmitting(false);
            return;
          }
          // Add the new selection
          newSelections = [...existingSelections, playerName];
        }
      }
      // Create predictions array for batch API (use value field)
      const predictions = newSelections.map(name => ({ value: name }));
      // If no selections left, use delete endpoint
      if (predictions.length === 0) {
        await deleteGeneralBet(betId);
        // Update local state
        const updatedBets = { ...userBets };
        Object.keys(updatedBets).forEach(key => {
          const bet = updatedBets[key];
          if (bet.bet_id === betId) {
            delete updatedBets[key];
          }
        });
        setUserBets(updatedBets);
      } else {
        // Use batch API
        const response = await placeBatchGeneralBets(betId, predictions);
        // Update local state
        const updatedBets = { ...userBets };
        // First remove existing bets for this bet ID
        Object.keys(updatedBets).forEach(key => {
          const bet = updatedBets[key];
          if (bet.bet_id === betId) {
            delete updatedBets[key];
          }
        });
        // Add new bets
        response.forEach((bet, index) => {
          const prediction = predictions[index];
          updatedBets[`${betId}_${prediction.value}`] = {
            ...bet,
            bet_id: betId,
            prediction
          };
        });
        setUserBets(updatedBets);
      }
      // Clear search term
      setSearchTerms({ ...searchTerms, [betId.toString()]: '' });
    } catch (err) {
      console.error('Error placing bet:', err);
      setError('Failed to save bet. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle removing a selection
  const handleRemoveSelection = async (betId: number, selectionId?: number, playerValue?: string) => {
    try {
      setSubmitting(true);
      const betConfig = betConfigs.find(config => config.id === betId);
      if (!betConfig || !betConfig.view_schema) {
        setError("Bet configuration not found");
        setSubmitting(false);
        return;
      }
      // For player bets, remove by value
      if (betConfig.view_schema.data === 'players' && playerValue) {
        const existingSelections = Object.values(userBets)
          .filter(bet => bet.bet_id === betId)
          .map(bet => bet.prediction?.value)
          .filter(Boolean);
        const newSelections = existingSelections.filter(name => name !== playerValue);
        const predictions = newSelections.map(name => ({ value: name }));
        if (predictions.length === 0) {
          await deleteGeneralBet(betId);
          const updatedBets = { ...userBets };
          Object.keys(updatedBets).forEach(key => {
            const bet = updatedBets[key];
            if (bet.bet_id === betId) {
              delete updatedBets[key];
            }
          });
          setUserBets(updatedBets);
        } else {
          const response = await placeBatchGeneralBets(betId, predictions);
          const updatedBets = { ...userBets };
          Object.keys(updatedBets).forEach(key => {
            const bet = updatedBets[key];
            if (bet.bet_id === betId) {
              delete updatedBets[key];
            }
          });
          response.forEach((bet, index) => {
            const prediction = predictions[index];
            updatedBets[`${betId}_${prediction.value}`] = {
              ...bet,
              bet_id: betId,
              prediction
            };
          });
          setUserBets(updatedBets);
        }
        setSubmitting(false);
        return;
      }
      // For team/match bets, remove by id
      const existingSelections = Object.values(userBets)
        .filter(bet => bet.bet_id === betId && bet.prediction?.id)
        .map(bet => bet.prediction.id)
        .filter(id => id !== selectionId);

      const predictions = existingSelections.map(id => ({ id }));

      if (predictions.length === 0) {
        await deleteGeneralBet(betId);
        const updatedBets = { ...userBets };
        Object.keys(updatedBets).forEach(key => {
          if (updatedBets[key].bet_id === betId) delete updatedBets[key];
        });
        setUserBets(updatedBets);
      } else {
        const response = await placeBatchGeneralBets(betId, predictions);
        const updatedBets = { ...userBets };
        Object.keys(updatedBets).forEach(key => {
          if (updatedBets[key].bet_id === betId) delete updatedBets[key];
        });
        response.forEach((bet, index) => {
          const prediction = predictions[index];
          updatedBets[`${betId}_${prediction.id}`] = { ...bet, bet_id: betId, prediction };
        });
        setUserBets(updatedBets);
      }
    } catch (err) {
      console.error('Error removing bet:', err);
      setError('Failed to remove bet. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Submit all group team selections for a group
  const submitGroupSelections = async (groupConfig: BetConfig) => {
    if (!groupConfig) return;
    
    try {
      setSubmitting(true);
      
      // Create prediction objects for all selected teams across all groups
      const predictions: { id: number }[] = [];
      
      Object.entries(groupSelections).forEach(([groupId, teamIds]) => {
        teamIds.forEach(teamId => {
          predictions.push({
            id: teamId
          });
        });
      });
      
      console.log("Submitting group selections:", predictions);
      
      // If we're clearing selections or there are no predictions, use delete endpoint
      if (predictions.length === 0) {
        await deleteGeneralBet(groupConfig.id);
        
        // Update local state by removing all bets for this config
        const updatedBets = { ...userBets };
        Object.keys(updatedBets).forEach(key => {
          if (parseInt(key.split('_')[0], 10) === groupConfig.id) {
            delete updatedBets[key];
          }
        });
        setUserBets(updatedBets);
        return;
      }
      
      // Submit batch predictions
      const response = await placeBatchGeneralBets(groupConfig.id, predictions);
      
      // Update local state with new bets
      const updatedBets = { ...userBets };
      
      // Remove existing bets for this config
      Object.keys(updatedBets).forEach(key => {
        if (parseInt(key.split('_')[0], 10) === groupConfig.id) {
          delete updatedBets[key];
        }
      });
      
      // Add new bets
      response.forEach((bet, index) => {
        const prediction = predictions[index];
        const betKey = `${groupConfig.id}_${prediction.id}`;
        updatedBets[betKey] = {
          ...bet,
          bet_id: groupConfig.id,
          prediction: prediction
        };
      });
      
      setUserBets(updatedBets);
    } catch (err) {
      console.error('Error submitting group selections:', err);
      setError('Failed to submit group selections. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle number input for general bets
  const handleNumberInput = async (betId: number, value: number) => {
    try {
      setSubmitting(true);
      
      // Create prediction for batch API
      const predictions = [{ value }];
      
      // Use batch API
      const response = await placeBatchGeneralBets(betId, predictions);
      
      // Update local state
      const updatedBets = { ...userBets };
      
      // First remove existing bets for this bet ID
      Object.keys(updatedBets).forEach(key => {
        const bet = updatedBets[key];
        if (bet.bet_id === betId) {
          delete updatedBets[key];
        }
      });
      
      // Add new bet
      if (response.length > 0) {
        // For numerical values, use a special key format including the value
        updatedBets[`${betId}_value_${value}`] = {
          ...response[0],
          bet_id: betId,
          prediction: { value }
        };
      }
      
      setUserBets(updatedBets);
    } catch (err) {
      console.error('Error placing bet:', err);
      setError('Failed to save bet. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Check if a bet is filled
  const isBetFilled = (betId?: number): boolean => {
    if (!betId) return false;
    
    // Get the bet config to check if multiple selections are required
    const betConfig = betConfigs.find(config => config.id === betId);
    if (!betConfig || !betConfig.view_schema) return false;
    
    // For multiple selections, check if we have the required number
    const requiredSize = betConfig.view_schema.size || 1;
    
    if (requiredSize > 1) {
      // Count how many selections we have for this bet
      const selectionCount = Object.values(userBets)
        .filter(bet => bet.bet_id === betId)
        .length;
      
      return selectionCount >= requiredSize;
    }
    
    // For single selection, check if we have any bets with this betId
    return Object.values(userBets).some(bet => bet.bet_id === betId);
  };
  
  // Get all selected teams for a bet
  const getSelectedTeams = (betId: number): { id: number; name: string; logo_url?: string; success?: boolean }[] => {
    return Object.values(userBets)
      .filter(bet => bet.bet_id === betId && bet.prediction?.id)
      .map(bet => {
        const team = teams.find(t => t.id === bet.prediction.id);
        return {
          id: bet.prediction.id,
          name: getTeamName(bet.prediction.id),
          logo_url: team?.logo_url,
          success: bet.success
        };
      });
  };
  
  // Get all selected players for a bet (by value)
  const getSelectedPlayers = (betId: number): { value: string; success?: boolean }[] => {
    return Object.values(userBets)
      .filter(bet => bet.bet_id === betId && bet.prediction?.value)
      .map(bet => ({ value: bet.prediction.value, success: bet.success }));
  };
  
  // Get team name by ID
  const getTeamName = (teamId: number): string => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : '';
  };
  
  // Get player name by ID
  const getPlayerName = (playerId: number): string => {
    const player = players.find(p => p.id === playerId);
    return player ? player.name : '';
  };
  
  // Get team or player details based on prediction
  const getSelectedName = (betId: number, type: string): string => {
    // Find first bet matching this betId
    const bet = Object.values(userBets).find(b => b.bet_id === betId);
    if (!bet || !bet.prediction || !bet.prediction.id) return '';
    
    if (type === 'team') {
      return getTeamName(bet.prediction.id);
    } else if (type === 'player') {
      return getPlayerName(bet.prediction.id);
    }
    
    return '';
  };
  
  // Add team selection toggle function for groups
  const toggleGroupTeamSelection = async (groupId: number, teamId: number) => {
    try {
      setSubmitting(true);
      
      // Get the groups config
      const groupConfig = betConfigs.find(config => config.view_schema?.data === 'groups');
      if (!groupConfig || !groupConfig.view_schema) {
        setError("Group configuration not found");
        setSubmitting(false);
        return;
      }
      
      // Get the maximum total selections based on view_schema.size
      const maxTotalSelections = groupConfig.view_schema.size || 16; // Default to 16 if not specified
      
      // Update local state
      const updatedSelections = { ...groupSelections };
      const currentTeams = [...(updatedSelections[groupId.toString()] || [])];
      
      // Check if team is already selected
      const teamIndex = currentTeams.indexOf(teamId);
      
      // Count total selections across all groups
      let currentTotalSelections = 0;
      Object.values(updatedSelections).forEach(teams => {
        currentTotalSelections += teams.length;
      });
      
      if (teamIndex >= 0) {
        // Remove team from selections
        currentTeams.splice(teamIndex, 1);
      } else {
        // Check if we've reached the maximum total selections across all groups
        if (currentTotalSelections >= maxTotalSelections) {
          // setError(`You can select a maximum of ${maxTotalSelections} teams total across all groups`);
          setSubmitting(false);
          return;
        }
        
        // Maximum teams per group is still 3 (or less if the total allows fewer)
        const maxPerGroup = Math.min(3, maxTotalSelections - (currentTotalSelections - currentTeams.length));
        
        // Don't allow more than 3 teams per group or the remaining allocation
        if (currentTeams.length >= maxPerGroup) {
          // setError(`You can select a maximum of ${maxPerGroup} teams per group`);
          setSubmitting(false);
          return;
        }
        
        // Add team to selections
        currentTeams.push(teamId);
      }
      
      // Update selections state
      updatedSelections[groupId.toString()] = currentTeams;
      setGroupSelections(updatedSelections);
      
      // Find the group config to submit changes
      if (groupConfig) {
        console.log('Group config found:', groupConfig);
        console.log('Updated selections:', updatedSelections);
        
        const allTeamSelections: { id: number; group_id: number }[] = [];
        
        // Collect all team selections across all groups
        Object.entries(updatedSelections).forEach(([gId, teamIds]) => {
          teamIds.forEach(tId => {
            allTeamSelections.push({
              id: tId,
              group_id: parseInt(gId, 10)
            });
          });
        });
        
        console.log('All team selections to submit:', allTeamSelections);
        
        // If we have no selections, delete all bets for this config
        if (allTeamSelections.length === 0) {
          await deleteGeneralBet(groupConfig.id);
          
          // Clear local state
          const updatedBets = { ...userBets };
          Object.keys(updatedBets).forEach(key => {
            if (key.startsWith(`${groupConfig.id}_`)) {
              delete updatedBets[key];
            }
          });
          setUserBets(updatedBets);
        } else {
          // Submit all selections in batch
          const response = await placeBatchGeneralBets(groupConfig.id, allTeamSelections);
          console.log('Batch bet response:', response);
          
          // Update local state
          const updatedBets = { ...userBets };
          
          // First remove existing bets for this config
          Object.keys(updatedBets).forEach(key => {
            if (key.startsWith(`${groupConfig.id}_`)) {
              delete updatedBets[key];
            }
          });
          
          // Add new bet entries
          allTeamSelections.forEach((selection, index) => {
            const betData = response[index] || {};
            const betKey = `${groupConfig.id}_${selection.id}`;
            updatedBets[betKey] = {
              ...(betData as any),
              bet_id: groupConfig.id,
              prediction: selection
            };
          });
          
          setUserBets(updatedBets);
        }
      }
    } catch (err) {
      console.error('Error updating group selection:', err);
      setError('Failed to update group selection. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Check if the group selections are valid (at least 2 teams per group)
  const isGroupSelectionsValid = (): boolean => {
    if (!groups || groups.length === 0) return false;
    
    // Get the groups config to check the size requirement
    const groupConfig = betConfigs.find(config => config.view_schema?.data === 'groups');
    if (!groupConfig || !groupConfig.view_schema) return false;
    
    // Get the required size
    const requiredSize = groupConfig.view_schema.size || 16;
    
    // Count total selections
    let totalSelections = 0;
    Object.values(groupSelections).forEach(teams => {
      totalSelections += teams.length;
    });
    
    // Check if total selections match the required size
    return totalSelections === requiredSize;
  };
  
  // Get numerical value for a bet
  const getNumberValue = (betId: number): number | undefined => {
    const bet = Object.values(userBets)
      .find(bet => bet.bet_id === betId && bet.prediction?.value !== undefined);
    
    return bet?.prediction?.value;
  };

  // Handle match selection
  const handleMatchSelect = async (betId: number, matchId: string) => {
    try {
      setSubmitting(true);
      
      // Get the bet config to check size constraints
      const betConfig = betConfigs.find(config => config.id === betId);
      if (!betConfig || !betConfig.view_schema) {
        setError("Bet configuration not found");
        setSubmitting(false);
        return;
      }
      
      // Find existing selections for this bet
      const existingSelections = Object.values(userBets)
        .filter(bet => bet.bet_id === betId)
        .map(bet => bet.prediction?.id)
        .filter(Boolean);
      
      // Check if this match is already selected
      const isAlreadySelected = existingSelections.includes(Number(matchId));
      
      // Check size constraints
      const requiredSize = betConfig.view_schema.size || 1;
      
      // Create new selections array
      let newSelections: string[] = [];
      
      if (isAlreadySelected) {
        // Remove the match if already selected
        newSelections = existingSelections.filter(id => id !== Number(matchId));
      } else {
        // For single selection, replace the existing selection
        if (requiredSize === 1) {
          newSelections = [matchId];
        } else {
          // For multiple selections, check if we've hit the limit
          if (existingSelections.length >= requiredSize) {
            setSelectionErrors(prev => ({ ...prev, [betId.toString()]: `ניתן לבחור מקסימום ${requiredSize} משחקים. הסר משחק כדי להוסיף אחר.` }));
            setSubmitting(false);
            return;
          }
          
          // Add the new selection
          newSelections = [...existingSelections, Number(matchId)];
        }
      }
      
      // Create predictions array for batch API
      const predictions = newSelections.map(id => ({ id }));
      
      // If no selections left, use delete endpoint
      if (predictions.length === 0) {
        await deleteGeneralBet(betId);
        
        // Update local state
        const updatedBets = { ...userBets };
        Object.keys(updatedBets).forEach(key => {
          const bet = updatedBets[key];
          if (bet.bet_id === betId) {
            delete updatedBets[key];
          }
        });
        setUserBets(updatedBets);
      } else {
        // Use batch API
        const response = await placeBatchGeneralBets(betId, predictions);
        
        // Update local state
        const updatedBets = { ...userBets };
        
        // First remove existing bets for this bet ID
        Object.keys(updatedBets).forEach(key => {
          const bet = updatedBets[key];
          if (bet.bet_id === betId) {
            delete updatedBets[key];
          }
        });
        
        // Add new bets
        response.forEach((bet, index) => {
          const prediction = predictions[index];
          updatedBets[`${betId}_${prediction.id}`] = {
            ...bet,
            bet_id: betId,
            prediction
          };
        });
        
        setUserBets(updatedBets);
      }
      
      // Clear search term
      setSearchTerms({ ...searchTerms, [betId.toString()]: '' });
    } catch (err) {
      console.error('Error placing bet:', err);
      setError('Failed to save bet. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Get all selected matches for a bet
  const getSelectedMatches = (betId: number): { id: string; description: string; success?: boolean }[] => {
    return Object.values(userBets)
      .filter(bet => bet.bet_id === betId && bet.prediction?.id)
      .map(bet => {
        const matchId = bet.prediction.id.toString();
        const match = matches.find(m => m.id === matchId);
        
        let description = 'Unknown match';
        if (match) {
          // Create description based on teams or placeholders
          if (match.home_team && match.away_team) {
            description = `${match.home_team.name} vs ${match.away_team.name}`;
          } else {
            description = `${match.home_team_placeholder} vs ${match.away_team_placeholder}`;
          }
        }
        
        return {
          id: matchId,
          description,
          success: bet.success
        };
      });
  };

  // Get match description by ID
  const getMatchDescription = (matchId: string): string => {
    const match = matches.find(m => m.id === matchId);
    if (!match) return 'Unknown match';
    
    // Create description based on teams or placeholders
    if (match.home_team && match.away_team) {
      return `${match.home_team.name} vs ${match.away_team.name}`;
    } else {
      return `${match.home_team_placeholder} vs ${match.away_team_placeholder}`;
    }
  };

  // Render content based on bet type
  const renderBetContent = (config: BetConfig) => {
    if (!config.view_schema) return null;
    
    const { data } = config.view_schema;
    const betId = config.id;
    const userBet = userBets[betId.toString()];
    const requiredSize = config.view_schema.size || 1;
    const isEditable = !tournamentStarted;
    
    switch (data) {
      case 'teams':
        return (
          <>
            {isEditable && (
              <>
                {selectionErrors[betId.toString()] && (
                  <p className="text-sm text-red-400 mb-2">{selectionErrors[betId.toString()]}</p>
                )}
                <SearchDropdown
                  placeholder="חפש קבוצה..."
                  value={searchTerms[betId.toString()] || ""}
                  onChange={(val) => handleSearch(betId.toString(), val)}
                  className="bg-dark-400 border-gray-700 text-white"
                  open={!!searchTerms[betId.toString()]}
                >
                  <div className="grid grid-cols-2 gap-2">
                    {teams
                      .filter(team => team.name.toLowerCase().includes((searchTerms[betId.toString()] || '').toLowerCase()))
                      .map(team => (
                        <div
                          key={team.id}
                          onClick={() => handleTeamSelect(betId, team.id)}
                          className="cursor-pointer hover:bg-gray-700 transition-colors p-3 border border-gray-700 rounded flex items-center justify-center gap-2 bg-gray-800 text-gray-200"
                        >
                          {team.logo_url && (
                            <img src={team.logo_url} alt={team.name} className="h-6 w-6" />
                          )}
                          <span>{team.name}</span>
                        </div>
                      ))
                    }
                  </div>
                </SearchDropdown>
              </>
            )}
            
            {requiredSize === 1 ? (
              // Single team selection
              isBetFilled(betId) && (
                <div className="space-y-2">
                  {getSelectedTeams(betId).map(team => (
                    <SelectedPickRow
                      key={team.id}
                      label={team.name}
                      logoUrl={team.logo_url}
                      success={team.success}
                      tournamentStarted={tournamentStarted}
                      onRemove={() => handleRemoveSelection(betId, team.id)}
                    />
                  ))}
                </div>
              )
            ) : (
              // Multiple team selection
              <div className="space-y-2">
                {getSelectedTeams(betId).map(team => (
                  <SelectedPickRow
                    key={team.id}
                    label={team.name}
                    logoUrl={team.logo_url}
                    success={team.success}
                    tournamentStarted={tournamentStarted}
                    onRemove={() => handleRemoveSelection(betId, team.id)}
                  />
                ))}
              </div>
            )}
          </>
        );

      case 'ranked_teams':
        return (
          <>
            {isEditable && !isBetFilled(betId) && (
              <div className="grid grid-cols-2 gap-2">
                {rankedTeams.map(team => (
                  <div
                    key={team.id}
                    onClick={() => handleTeamSelect(betId, team.id)}
                    className="cursor-pointer hover:bg-gray-700 transition-colors p-3 border border-gray-700 rounded flex items-center justify-center gap-2 bg-gray-800 text-gray-200"
                  >
                    {team.logo_url && (
                      <img src={team.logo_url} alt={team.name} className="h-6 w-6" />
                    )}
                    <span>{team.name}</span>
                  </div>
                ))}
              </div>
            )}

            {isBetFilled(betId) && (
              <div className="space-y-2">
                {getSelectedTeams(betId).map(team => (
                  <SelectedPickRow
                    key={team.id}
                    label={team.name}
                    logoUrl={team.logo_url}
                    success={team.success}
                    tournamentStarted={tournamentStarted}
                    onRemove={() => handleRemoveSelection(betId, team.id)}
                  />
                ))}
              </div>
            )}
          </>
        );

      case 'players':
        return (
          <>
            {isEditable && (
              <>
                <SearchDropdown
                  placeholder="חפש שחקן..."
                  value={searchTerms[betId.toString()] || ""}
                  onChange={(val) => handleSearch(betId.toString(), val)}
                  className="bg-dark-400 border-gray-700 text-white"
                  open={!!searchTerms[betId.toString()]}
                >
                  <div className="grid grid-cols-2 gap-2">
                    {playerSearchResults.map(player => (
                      <div
                        key={player.id}
                        onClick={() => handlePlayerSelect(betId, player.name)}
                        className="cursor-pointer hover:bg-gray-700 transition-colors p-3 border border-gray-700 rounded bg-gray-800 text-gray-200 text-center"
                      >
                        <div>{player.name}</div>
                      </div>
                    ))}
                    {playerSearchResults.length === 0 && (
                      <div className="text-gray-500 p-2">לא נמצאו שחקנים.</div>
                    )}
                  </div>
                </SearchDropdown>
              </>
            )}
            
            {requiredSize === 1 ? (
              // Single player selection
              isBetFilled(betId) && (
                <div className="space-y-2">
                  {getSelectedPlayers(betId).map(player => (
                    <SelectedPickRow
                      key={player.value}
                      label={player.value}
                      success={player.success}
                      tournamentStarted={tournamentStarted}
                      onRemove={() => handleRemoveSelection(betId, undefined, player.value)}
                    />
                  ))}
                </div>
              )
            ) : (
              // Multiple player selection
              <div className="space-y-2">
                {isEditable && (
                  <p className="text-sm text-gray-400">Select {requiredSize} players</p>
                )}
                {getSelectedPlayers(betId).map(player => (
                  <SelectedPickRow
                    key={player.value}
                    label={player.value}
                    success={player.success}
                    tournamentStarted={tournamentStarted}
                    onRemove={() => handleRemoveSelection(betId, undefined, player.value)}
                  />
                ))}
                <div className="text-sm text-gray-400 mt-2">
                  {getSelectedPlayers(betId).length} of {requiredSize} players selected
                </div>
              </div>
            )}
          </>
        );
        
      case 'groups': {
        const standingsConfig = betConfigs.find(c => c.type === 'group_standings');
        if (!standingsConfig) return null;
        return (
          <GroupRankingBet
            qualConfig={config}
            standingsConfig={standingsConfig}
            groups={groups}
            teams={teams}
            userBets={userBets}
            bettable={isEditable}
            onBetsUpdated={(standingsBets, qualBets) => {
              setUserBets(prev => {
                const updated = { ...prev };
                if (standingsBets.length > 0) {
                  Object.keys(updated).forEach(key => {
                    if (updated[key].bet_id === standingsConfig.id) delete updated[key];
                  });
                }
                Object.keys(updated).forEach(key => {
                  if (updated[key].bet_id === config.id) delete updated[key];
                });
                [...standingsBets, ...qualBets].forEach(bet => {
                  if (bet.prediction?.id != null) {
                    updated[`${bet.bet_id}_${bet.prediction.id}`] = bet;
                  }
                });
                return updated;
              });
            }}
          />
        );
      }
        
      case 'number':
        if (!isEditable) {
          const value = getNumberValue(betId);
          return (
            <div className="text-gray-200 text-lg font-semibold">
              {value !== null && value !== undefined ? value : 'לא נבחר ערך'}
            </div>
          );
        }

        return (
          <>
            <Input
              type="number"
              placeholder="Enter a number"
              value={getNumberValue(betId) || ""}
              onChange={(e) => handleNumberInput(betId, parseInt(e.target.value, 10))}
              className="mb-2 bg-dark-400 border-gray-700 text-white"
            />
          </>
        );
        
      case 'match':
        return (
          <>
            {isEditable && (
              <>
                <SearchDropdown
                  placeholder="חפש משחק"
                  value={searchTerms[betId.toString()] || ""}
                  onChange={(val) => handleSearch(betId.toString(), val)}
                  className="bg-dark-400 border-gray-700 text-white"
                  open={!!searchTerms[betId.toString()]}
                >
                  <div className="grid grid-cols-1 gap-2">
                    {matches
                      .filter(match => {
                        const searchTerm = (searchTerms[betId.toString()] || '').toLowerCase();
                        const homeTeamName = match.home_team?.name || match.home_team_placeholder;
                        const awayTeamName = match.away_team?.name || match.away_team_placeholder;
                        return homeTeamName.toLowerCase().includes(searchTerm) ||
                               awayTeamName.toLowerCase().includes(searchTerm);
                      })
                      .map(match => {
                        const homeTeamName = match.home_team?.name || match.home_team_placeholder;
                        const awayTeamName = match.away_team?.name || match.away_team_placeholder;
                        const matchDate = new Date(match.match_date).toLocaleDateString();
                        return (
                          <div
                            key={match.id}
                            onClick={() => handleMatchSelect(betId, match.id)}
                            className="cursor-pointer hover:bg-gray-700 transition-colors p-3 border border-gray-700 rounded bg-gray-800 text-gray-200"
                          >
                            <div className="flex justify-between">
                              <span>{homeTeamName} vs {awayTeamName}</span>
                              <span className="text-gray-400 text-sm">{matchDate}</span>
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {match.round} - {match.status}
                            </div>
                          </div>
                        );
                      })
                    }
                  </div>
                </SearchDropdown>
              </>
            )}
            
            {requiredSize === 1 ? (
              // Single match selection
              isBetFilled(betId) && (
                <div className="space-y-2">
                  {getSelectedMatches(betId).map(match => (
                    <SelectedPickRow
                      key={match.id}
                      label={match.description}
                      success={match.success}
                      tournamentStarted={tournamentStarted}
                      onRemove={() => handleRemoveSelection(betId, Number(match.id))}
                    />
                  ))}
                </div>
              )
            ) : (
              // Multiple match selection
              <div className="space-y-2">
                <p className="text-sm text-gray-400">בחר {requiredSize} משחקים</p>
                {getSelectedMatches(betId).map(match => (
                  <SelectedPickRow
                    key={match.id}
                    label={match.description}
                    success={match.success}
                    tournamentStarted={tournamentStarted}
                    onRemove={() => handleRemoveSelection(betId, Number(match.id))}
                  />
                ))}
                <div className="text-sm text-gray-400 mt-2">
                  {getSelectedMatches(betId).length} of {requiredSize} matches selected
                </div>
              </div>
            )}
          </>
        );
        
      default:
        return <p className="text-gray-300">Unsupported bet type</p>;
    }
  };

  // Get stats for a general bet config to display points and status
  const getGeneralBetStats = (config: BetConfig) => {
    const betsForConfig = Object.values(userBets).filter(bet => bet.bet_id === config.id);
    const successCount = betsForConfig.filter(bet => bet.success === true).length;
    const failureCount = betsForConfig.filter(bet => bet.success === false).length;
    const pendingCount = betsForConfig.filter(bet => bet.success === null || bet.success === undefined).length;

    const totalSelections = betsForConfig.length || 0;
    const totalPoints = totalSelections * config.points;
    const earnedPoints = successCount * config.points;

    return {
      totalSelections,
      totalPoints,
      earnedPoints,
      successCount,
      failureCount,
      pendingCount
    };
  };

  if (loading) {
    return <div className="text-center py-8">Loading bets...</div>;
  }

  if (error) {
    return <div className="bg-red-100 text-red-800 p-4 rounded">{error}</div>;
  }

  if (betConfigs.length === 0) {
    return <div className="text-center py-8">No general bets available</div>;
  }

  return (
    <div className={`space-y-4 max-w-xl mx-auto ${className || ''}`}>
      {tournamentStarted && !userId && (
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center gap-4">
          <label className="text-gray-300 font-medium">בחר משתמש:</label>
          {loadingUsers ? (
            <div className="text-gray-400">טוען משתמשים...</div>
          ) : (
            <div className="relative min-w-[220px]" ref={userDropdownRef}>
              <button
                onClick={() => { setUserDropdownOpen(o => !o); setUserSearch(''); }}
                className="w-full flex items-center justify-between bg-gray-800 border border-gray-700 text-white rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <span className={internalUserId ? 'text-white' : 'text-gray-400'}>
                  {users.find(u => u.id === internalUserId)
                    ? `${users.find(u => u.id === internalUserId)!.first_name} ${users.find(u => u.id === internalUserId)!.last_name}`
                    : 'בחר משתמש'}
                </span>
                <span className="text-gray-400 text-2xl ml-2">▾</span>
              </button>

              {userDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-gray-900 border border-gray-700 rounded-md shadow-lg">
                  <div className="p-2 border-b border-gray-700">
                    <Input
                      autoFocus
                      placeholder="חפש משתמש..."
                      value={userSearch}
                      onChange={e => setUserSearch(e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div className="max-h-52 overflow-y-auto p-1">
                    {users
                      .filter(u => `${u.first_name} ${u.last_name}`.toLowerCase().includes(userSearch.toLowerCase()))
                      .map(u => (
                        <div
                          key={u.id}
                          onClick={() => { setInternalUserId(u.id); setUserDropdownOpen(false); setUserSearch(''); }}
                          className={`cursor-pointer px-3 py-2 rounded text-gray-200 hover:bg-gray-700 ${internalUserId === u.id ? 'bg-gray-700' : ''}`}
                        >
                          {u.first_name} {u.last_name}
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {!tournamentStarted && (() => {
        const standingsCfg = betConfigs.find(c => c.type === 'group_standings');
        const allFilled = betConfigs
          .filter(c => c.type !== 'group_standings')
          .every(c => {
            const filled = isBetFilled(c.id);
            if (c.type === 'group_qualification') {
              return filled && (!standingsCfg || isBetFilled(standingsCfg.id));
            }
            return filled;
          });
        return (
          <div className={`mb-4 px-4 py-3 rounded-lg text-center font-semibold text-sm border ${
            allFilled
              ? 'bg-green-900 border-green-500 text-green-300'
              : 'bg-red-900 border-red-500 text-red-300'
          }`}>
            {allFilled ? '✓ הכל תקין, אתה מוכן לטורניר' : '⚠ שים לב, לא מילאת את כל התחזיות שלך לטורניר.'}
          </div>
        );
      })()}
      <Accordion
        type="multiple"
        className="w-full"
        value={openItems}
        onValueChange={(values) =>
          setOpenItems(Array.isArray(values) ? values : [values])
        }
      >
        {betConfigs.filter(config => config.type !== 'group_standings').map(config => {
          let stats = getGeneralBetStats(config);
          if (config.type === 'group_qualification') {
            const standingsCfg = betConfigs.find(c => c.type === 'group_standings');
            if (standingsCfg) {
              const sStats = getGeneralBetStats(standingsCfg);
              stats = {
                totalSelections: stats.totalSelections + sStats.totalSelections,
                totalPoints: stats.totalPoints + sStats.totalPoints,
                earnedPoints: stats.earnedPoints + sStats.earnedPoints,
                successCount: stats.successCount + sStats.successCount,
                failureCount: stats.failureCount + sStats.failureCount,
                pendingCount: stats.pendingCount + sStats.pendingCount,
              };
            }
          }
          const hasBet = stats.totalSelections > 0;

          const standingsCfgForFilled = config.type === 'group_qualification'
            ? betConfigs.find(c => c.type === 'group_standings')
            : undefined;
          const isFilled = isBetFilled(config.id) &&
            (!standingsCfgForFilled || isBetFilled(standingsCfgForFilled.id));

          let badgeLabel: string | null = null;
          let badgeClass = 'ml-2 bg-gray-700 text-gray-200';

          if (!tournamentStarted) {
            if (isFilled) {
              badgeLabel = 'תקין';
              badgeClass = 'ml-2 border border-green-400 text-green-300 bg-green-900';
            } else {
              badgeLabel = 'לא תקין';
              badgeClass = 'ml-2 bg-gray-700 text-gray-200';
            }
          } else if (hasBet) {
            // After tournament start: show success / failure / pending
            if (stats.pendingCount === stats.totalSelections) {
              badgeLabel = 'ממתין';
              badgeClass = 'ml-2 bg-gray-700 text-gray-200';
            } else if (stats.earnedPoints === stats.totalPoints && stats.totalPoints > 0) {
              // Full hit: all possible points
              badgeLabel = 'פגיעה';
              badgeClass = 'ml-2 border border-green-400 text-green-300 bg-green-900';
            } else if (stats.earnedPoints > 0 && stats.earnedPoints < stats.totalPoints) {
              // Partial hit: some points but not all
              badgeLabel = 'חלקי';
              badgeClass = 'ml-2 border border-blue-400 text-blue-300 bg-blue-900';
            } else {
              // No points at all
              badgeLabel = 'כלום';
              badgeClass = 'ml-2 border border-red-400 text-red-300 bg-red-900';
            }
          }

          const showPoints = config.points > 0 && hasBet && tournamentStarted;

          let pointsText: string;
          if (config.type === 'group_qualification') {
            const standingsCfg = betConfigs.find(c => c.type === 'group_standings');
            if (standingsCfg) {
              if (showPoints) {
                // stats is already combined (qual + standings) from lines above
                pointsText = `${stats.earnedPoints}/${stats.totalPoints} נק׳`;
              } else {
                pointsText = `${config.points} / ${standingsCfg.points} נק׳`;
              }
            } else {
              pointsText = showPoints ? `${stats.earnedPoints}/${stats.totalPoints} נק׳` : `${config.points} נק׳`;
            }
          } else {
            pointsText = showPoints ? `${stats.earnedPoints}/${stats.totalPoints} נק׳` : `${config.points} נק׳`;
          }

          const validationLines: string[] = [];
          if (!tournamentStarted && !isFilled) {
            if (config.type === 'group_qualification') {
              const standingsCfg = betConfigs.find(c => c.type === 'group_standings');
              const thirdPlaceSlots = config.view_schema ? config.view_schema.size - groups.length * 2 : 0;
              const standingsBetCount = standingsCfg
                ? Object.values(userBets).filter(b => b.bet_id === standingsCfg.id).length
                : 0;
              const qualBetCount = Object.values(userBets).filter(b => b.bet_id === config.id).length;
              const thirdPlaceSelected = Math.max(0, qualBetCount - groups.length * 2);
              if (standingsBetCount === 0) validationLines.push('יש לדרג את הקבוצות בכל בית');
              if (thirdPlaceSlots > 0 && thirdPlaceSelected < thirdPlaceSlots)
                validationLines.push(`${thirdPlaceSelected} מתוך ${thirdPlaceSlots} קבוצות שלישיות נבחרו לעלות`);
            } else {
              const isPlayerBet = config.view_schema?.data === 'players';
              const requiredSize = config.view_schema?.size || 1;
              if (isPlayerBet) validationLines.push('יש לבחור שחקן');
              else if (config.type === 'high_score_match') validationLines.push('יש לבחור משחק');
              else if (requiredSize > 1) validationLines.push(`יש לבחור ${requiredSize} קבוצות`);
              else validationLines.push('יש לבחור קבוצה');
            }
          }
          const validationTitle = validationLines.join('\n') || undefined;
          const validationErrors = validationLines.length > 0 ? (
            <div className="mb-2 space-y-1">
              {validationLines.map((line, i) => (
                <p key={i} className="text-sm text-red-400 text-right">{line}</p>
              ))}
            </div>
          ) : null;

          return (
            <AccordionItem value={`item-${config.id}`} key={config.id}>
              <AccordionTrigger>
                <div className="flex justify-between w-full items-center">
                  {/* Config name stays on the left */}
                  <span className="text-right truncate flex-1 min-w-0">
                    {config.name}
                  </span>

                  {/* Points + Badge grouped together on the right */}
                  <div className="flex items-center space-x-2">
                    {config.points > 0 && (
                      <span className="ml-2 text-sm text-stake-blue font-semibold">
                        {pointsText}
                      </span>
                    )}
                    {badgeLabel && (
                      <Badge
                        variant={tournamentStarted ? 'secondary' : 'outline'}
                        className={badgeClass}
                        title={validationTitle}
                      >
                        {badgeLabel}
                      </Badge>
                    )}
                  </div>
                </div>
              </AccordionTrigger>

              <AccordionContent>
                <div className="py-2">
                  {!tournamentStarted && (
                    <p className="text-gray-300 mb-4">{config.description}</p>
                  )}
                  {validationErrors}
                  {config.type === 'group_qualification' ? (
                    <div className="overflow-y-auto max-h-[60vh]">
                      {renderBetContent(config)}
                    </div>
                  ) : (
                    renderBetContent(config)
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};

export default GeneralBetsList; 